import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { backendAccessGet } from "../Utils/Authentication"
import { backendAccessPost } from "../Utils/Authentication"

const PopupModal = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white w-[90vw] max-w-4xl max-h-[85vh] rounded-xl shadow-xl overflow-hidden">
        <div className="flex justify-between items-center px-4 py-3 border-b">
          <h3 className="font-semibold">Ticket Details</h3>
          <button
            onClick={onClose}
            className="px-2 py-1 rounded hover:bg-gray-200"
          >
            ✕
          </button>
        </div>

        {/* Scrollable content */}
        <div className="p-4 overflow-y-auto overflow-x-hidden max-h-[75vh]">
          {children}
        </div>
      </div>
    </div>
  );
};

const TicketCard = ({
  title,
  status,
  tickets,
  adminUsers,
  statusColors,
  onTicketClick,
  onStatusChange,
  onAssign,
  onArchive,
  onReopen
}) => {
  const isArchived = status === 'archived';
  
  return (
    <div className={`p-5 rounded-xl shadow ${
      status === 'open' ? 'bg-blue-50' :
      status === 'processing' ? 'bg-yellow-50' :
      status === 'close' ? 'bg-green-50' :
      'bg-gray-100'
    }`}>
      <h3 className={`pb-2 mb-4 border-b ${statusColors[status]} font-medium`}>
        {title} ({tickets.length})
      </h3>
      {tickets.length === 0 ? (
        <p className="text-gray-500 text-sm">
          {isArchived ? 'No archived tickets' : `No ${status} tickets`}
        </p>
      ) : (
        tickets.map((ticket) => (
          <div
            key={ticket.id}
            onClick={() => onTicketClick(ticket)}
            className={`border border-gray-200 p-4 mb-4 rounded-lg cursor-pointer hover:bg-${
              status === 'open' ? 'blue' :
              status === 'processing' ? 'yellow' :
              status === 'close' ? 'green' :
              'gray'
            }-100 transition`}
          >
            <div className="font-medium text-gray-900">{ticket.referenceID}</div>
            <div className="text-sm text-gray-700 mt-1">{ticket.subject}</div>
            <div className="text-xs text-gray-500 mt-2">{ticket.last_updated}</div>
            
            {status !== 'open' && (
              <div className="text-xs text-gray-600 mt-1">
                Assigned to: <span className="font-medium">{ticket.assigned_to || 'Unassigned'}</span>
              </div>
            )}
            
            <div className="flex items-center mt-3 gap-2">
              {status === 'open' && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onStatusChange(ticket.referenceID, 'processing');
                    }}
                    className="px-3 py-1.5 bg-yellow-100 text-black rounded text-xs hover:bg-yellow-200 transition"
                  >
                    Assign
                  </button>
                  <select
                    onChange={(e) => onAssign(ticket.referenceID, e.target.value)}
                    className="ml-2 px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  >
                    <option value="">Assign To...</option>
                    {adminUsers.map((admin) => (
                      <option key={admin.id} value={admin.username}>
                        {admin.username} ({admin.email})
                      </option>
                    ))}
                  </select>
                </>
              )}
              
              {status === 'processing' && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onStatusChange(ticket.referenceID, 'close');
                  }}
                  className="px-3 py-1.5 bg-green-100 text-black rounded text-xs hover:bg-green-200 transition"
                >
                  Close
                </button>
              )}
              
              {status === 'close' && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onArchive(ticket.referenceID);
                  }}
                  className="px-3 py-1.5 bg-gray-500 text-white rounded text-xs hover:bg-gray-600 transition"
                >
                  Archive
                </button>
              )}
              
              {isArchived && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onReopen(ticket.referenceID);
                  }}
                  className="px-3 py-1.5 bg-blue-500 text-white rounded text-xs hover:bg-blue-600 transition"
                >
                  Reopen
                </button>
              )}
              
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onTicketClick(ticket);
                }}
                className="ml-auto px-3 py-1.5 bg-black text-white rounded text-xs hover:bg-gray-800 transition"
              >
                {isArchived ? 'View Details' : 'Reply'}
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

const AdminDashboard = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [adminUsers, setAdminUsers] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [showArchived, setShowArchived] = useState(true);
  const [file, setFile] = useState(null);
  const [isSubmittingReply, setIsSubmittingReply] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const authData = localStorage.getItem('auth');
    const userData = localStorage.getItem('me');

    if (!authData || !userData) {
      navigate('/');
      return;
    }

    try {
      const user = JSON.parse(userData);
      setCurrentUser(user);
      fetchAdminUsers();
      fetchTickets();
    } catch (e) {
      console.error('Invalid auth data', e);
      navigate('/');
    }
  }, []);

  const fetchAdminUsers = async () => {
    try {
      const res = await backendAccessGet('/api/tickets/admin/users/');
      setAdminUsers(res);
    } catch (err) {
      console.error('Failed to load admin users:', err);
      if (err.message.includes('token')) {
        navigate('/');
      }
    }
  };

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const res = await backendAccessGet('/api/tickets/all-references/');
      setTickets(res);
    } catch (err) {
      console.error('Error fetching tickets:', err);
      if (err.message.includes('token') || err.status === 401 || err.status === 403) {
        localStorage.removeItem('authToken');
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (referenceID, newStatus) => {
    try {
      const thread = await backendAccessGet(`/api/tickets/reference/${referenceID}/`);
      const latestTicket = thread[0];

      await backendAccessPost(
        '/api/tickets/update-status/',
        { id: latestTicket.id, status: newStatus },
        { 'Content-Type': 'application/json' }
      );

      fetchTickets();
    } catch (err) {
      alert('Failed to update status');
    }
  };

  const handleAssign = async (referenceID, assignedToUsername) => {
    try {
      const thread = await backendAccessGet(`/api/tickets/reference/${referenceID}/`);
      const latestTicket = thread[0];

      await backendAccessPost(
        '/api/tickets/assign/',
        { id: latestTicket.id, assigned_to: assignedToUsername },
        { 'Content-Type': 'application/json' }
      );

      if (latestTicket.status === 'open') {
        await backendAccessPost(
          '/api/tickets/update-status/',
          { id: latestTicket.id, status: 'processing' },
          { 'Content-Type': 'application/json' }
        );
      }

      fetchTickets();
    } catch (err) {
      console.error('Assignment failed:', err);
      alert('Failed to assign ticket');
    }
  };

  const archiveTicket = async (ticketId) => {
    if (!window.confirm('Are you sure you want to archive this ticket? It will disappear from the Close card.')) return;
    try {
      await axios.put('/api/tickets/archive/', {
        referenceID: ticketId
      });
      fetchTickets();
    } catch (err) {
      alert('Failed to archive ticket');
    }
  };

  const reopenTicket = async (ticketId) => {
    if (!window.confirm('Are you sure you want to reopen this archived ticket? It will return to Processing.')) return;

    try {
      await axios.put('/api/tickets/reopen/', {
        referenceID: ticketId
      });
      fetchTickets();
    } catch (err) {
      alert('Failed to reopen ticket');
    }
  };

  const loadThread = async (ticket) => {
    try {
      const thread = await backendAccessGet(`/api/tickets/reference/${ticket.referenceID}/`);
      setSelectedThread({ ...ticket, messages: thread });
    } catch (err) {
      console.error('Failed to load thread:', err);
      alert('Failed to load ticket details');
    }
  };

  const submitReply = async () => {
    if (!replyText.trim() || isSubmittingReply) return;

    setIsSubmittingReply(true);

    try {
      const fileData = file ? await readFileAsBase64(file) : null;

      const payload = {
        name: currentUser.username,
        family: '',
        email: currentUser.email,
        subject: `Re: ${selectedThread.subject}`,
        description: replyText,
        parent_referenceID: selectedThread.referenceID,
        status: 'processing',
        source: selectedThread.source || 'ETD',
        file: fileData,
      };

      await backendAccessPost(
        '/api/tickets/create/',
        payload,
        { 'Content-Type': 'application/json' }
      );

      setReplyText('');
      setFile(null);
      setSelectedThread(null);
      fetchTickets();
    } catch (err) {
      console.error('Reply error:', err);
      alert('Failed to send reply: ' + (err.message || 'Unknown error'));
    } finally {
      setIsSubmittingReply(false);
    }
  };

  const readFileAsBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const logout = () => {
    localStorage.removeItem('auth');
    localStorage.removeItem('me');
    navigate('/');
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen">
      <p className="text-gray-600">Loading...</p>
    </div>
  );

  const open = tickets.filter(t => t.status === 'open');
  const processing = tickets.filter(t => t.status === 'processing');
  const close = tickets.filter(t => t.status === 'close');
  const archived = tickets.filter(t => t.status === 'archived');

  const statusColors = {
    open: 'text-blue-500 border-blue-500',
    processing: 'text-orange-500 border-orange-500',
    close: 'text-green-500 border-green-500',
    archived: 'text-gray-500 border-gray-500'
  };

  return (
<div className="bg-white min-h-screen flex flex-col">
         <div className="flex justify-between items-center p-[20px]">
        <h1 className="text-xl font-semibold">Support Dashboard</h1>


        <div className="flex  space-x-[10px]">
          <button
            onClick={() => navigate('/dashboard')}
            className="w-full text-left px-4 py-2 hover:bg-gray-100"
          >
            Dashboard
          </button>

          <button
            onClick={logout}
            className="w-full text-left px-4 py-2 text-red-600 hover:bg-gray-100"
          >
            Logout
          </button>
        </div>

      </div>



<PopupModal
  isOpen={!!selectedThread}
  onClose={() => setSelectedThread(null)}
>
  {selectedThread && (
    <div className="w-full min-w-0">
      <h3 className="text-xl font-bold mb-4">
        Ticket #{selectedThread.referenceID} – {selectedThread.subject}
      </h3>

      {/* Messages */}
      <div className="space-y-4 mb-6 bg-gray-50 p-3 rounded border max-h-72 overflow-y-auto overflow-x-hidden">
        {[...(selectedThread.messages || [])].reverse().map((msg) => (
          <div key={msg.id} className="p-3 border-b last:border-0 min-w-0">
            <div className="flex flex-col gap-1 text-sm mb-1 sm:flex-row sm:justify-between">
              <div className="min-w-0">
                <div className="font-medium">
                  {msg.data.name || msg.data.email}
                </div>
                <div className="whitespace-pre-wrap break-words text-gray-700">
                  {msg.data.description}
                </div>
              </div>
              <span className="shrink-0 text-gray-500">
                {new Date(msg.created_at).toLocaleString()}
              </span>
            </div>

            {msg.file && (
              <a
                href={msg.file}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 text-sm underline"
              >
                📎 View attachment
              </a>
            )}
          </div>
        ))}
      </div>

      {/* Reply */}
      <textarea
        value={replyText}
        onChange={(e) => setReplyText(e.target.value)}
        rows="4"
        wrap="soft"
        className="w-full border rounded p-2 mb-3 whitespace-pre-wrap break-words overflow-x-hidden resize-y"
        placeholder="Type reply..."
      />

      <input
        type="file"
        onChange={(e) => setFile(e.target.files?.[0] || null)}
        className="mb-3"
      />

      <div className="flex justify-end gap-2">
        <button
          onClick={() => setSelectedThread(null)}
          className="px-4 py-2 bg-gray-300 rounded"
        >
          Cancel
        </button>
        <button
          onClick={submitReply}
          disabled={!replyText.trim() || isSubmittingReply}
          className="px-4 py-2 bg-blue-600 text-white rounded disabled:bg-gray-400"
        >
          {isSubmittingReply ? 'Sending...' : 'Send'}
        </button>
      </div>
    </div>
  )}
</PopupModal>


      {archived.length > 0 && (
        <div className="mt-8">
  

            <div className="mt-4 p-5 bg-gray-100 rounded-xl border border-gray-300">
              <TicketCard
                title="Archived Tickets"
                status="archived"
                tickets={archived}
                adminUsers={adminUsers}
                statusColors={statusColors}
                onTicketClick={loadThread}
                onReopen={reopenTicket}
              />
            </div>
          )
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
